import { CrmTaskStatus } from '@plato/data/prisma';
import { formatMonthShortDay } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import {
    Card,
    CardComplete,
    CardContent,
    CardDetail,
    CardFront,
    CardHeader,
    CardMainAction,
    CardSubtitle,
    CardTitle,
} from '@plato/ui/containers/card';
import { cn } from '@plato/ui/util/classes';
import { CheckIcon, UserIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmTaskDTO } from '../../../server/dto/crm-task-dto.ts';
import { isTaskDueToday, isTaskOverdue } from '../../../utils/task-helpers.ts';
import type { TaskCompletedSource } from '../../analytics/types/analytics-events.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { useToggleTask } from '../../tasks/hooks/use-toggle-task.tsx';
import { deriveTaskText } from '../../tasks/lib/derive-task-text.ts';

export type ActivityFeedTaskItemProps = {
    payload: CrmTaskDTO;
    source: TaskCompletedSource;
};

/**
 * One task: its first line as the title, the rest as a two-line preview, and assignee · due
 * date (completion date once done). Opens the task dialog.
 *
 * Takes the task itself rather than a feed record, so the upcoming list and a visit's
 * logged tasks — which never see a feed record — render the same card.
 */
export function ActivityFeedTaskItem({ payload: task, source }: ActivityFeedTaskItemProps): ReactElement {
    const { t } = useTranslation('activity-feed');
    const locale = useResolvedLocale();
    const { openTaskDetails } = useDialogs();
    const { pendingTasks, setTaskComplete } = useToggleTask(source);

    const completed = task.status === CrmTaskStatus.COMPLETED;
    const pending = pendingTasks.includes(task.taskId);
    const { title, detail } = deriveTaskText(task.descriptionRich);
    const date = completed ? (task.completedAt ?? task.dueDate) : task.dueDate;
    const dueColor = completed
        ? undefined
        : isTaskOverdue(task)
          ? 'text-danger'
          : isTaskDueToday(task)
            ? 'text-warning'
            : undefined;

    return (
        <Card>
            <CardFront className="card-completed:no-underline!">
                <CardComplete
                    variant="primary"
                    checked={completed}
                    disabled={pending}
                    onChange={(event) => {
                        setTaskComplete(task.taskId, event.target.checked);
                    }}
                />
                <CardContent>
                    <CardHeader className="items-start @md:items-start">
                        <CardTitle className="max-w-full flex-1 truncate card-completed:line-through">
                            {title}
                        </CardTitle>
                        <CardSubtitle className="tabular-nums">
                            <UserIcon className="size-3.5 shrink-0" />
                            <span className="truncate">{task.assignedTo.fullName}</span>
                            {date && (
                                <>
                                    {' · '}
                                    <span className={cn('flex items-center gap-tight', dueColor)}>
                                        {completed && <CheckIcon className="size-3.5 shrink-0" />}
                                        {formatMonthShortDay(date, locale)}
                                    </span>
                                </>
                            )}
                        </CardSubtitle>
                    </CardHeader>
                    {detail && <CardDetail className="line-clamp-2">{detail}</CardDetail>}
                </CardContent>
                <CardMainAction
                    aria-label={t('card__open', { name: title })}
                    onClick={() => {
                        openTaskDetails(task.taskId);
                    }}
                />
            </CardFront>
        </Card>
    );
}
